import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import tokenExpirationHandler from './tokenExpirationHandler';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';
const ENCRYPTION_KEY = process.env.REACT_APP_PAYLOAD_ENCRYPTION_KEY || 'default-payload-encryption-key-32-chars';
const HASH_SALT = process.env.REACT_APP_PAYLOAD_HASH_SALT || 'default-hash-salt-value-must-be-changed';
const ENV_ENCRYPTION_ENABLED = process.env.REACT_APP_ENABLE_PAYLOAD_ENCRYPTION === 'true';

// Helper to convert base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

// Helper to convert ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Get Web Crypto Key for AES-GCM
async function getCryptoKey(secret: string): Promise<CryptoKey> {
  const rawKey = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret));
  return window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

// Encrypt payload using AES-256-GCM
async function encryptPayload(text: string): Promise<string> {
  if (!text || text.trim() === '') return text;
  
  const key = await getCryptoKey(ENCRYPTION_KEY);
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV is standard for GCM
  
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(text)
  );
  
  // Combine: IV (12 bytes) + Encrypted Buffer (Ciphertext + 16-byte Auth Tag)
  const combined = new Uint8Array(12 + encryptedBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encryptedBuffer), 12);
  
  return arrayBufferToBase64(combined.buffer);
}

// Decrypt payload using AES-256-GCM
async function decryptPayload(encryptedBase64: string): Promise<string> {
  if (!encryptedBase64 || encryptedBase64.trim() === '') return encryptedBase64;
  
  const key = await getCryptoKey(ENCRYPTION_KEY);
  const combined = new Uint8Array(base64ToArrayBuffer(encryptedBase64));
  
  if (combined.length < 28) { // 12 bytes IV + 16 bytes tag minimum
    throw new Error('Invalid encrypted payload size');
  }
  
  const iv = combined.slice(0, 12);
  const ciphertextAndTag = combined.slice(12);
  
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertextAndTag
  );
  
  return new TextDecoder().decode(decryptedBuffer);
}

// Generate HMAC-SHA256 signature
async function generateSignature(ciphertext: string, salt: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(salt),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signatureBuffer = await window.crypto.subtle.sign(
    'HMAC',
    key,
    enc.encode(ciphertext)
  );
  return Array.from(new Uint8Array(signatureBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

class ApiClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  
  // Debug bypass users list and loading status
  private encryptionEnabled: boolean = ENV_ENCRYPTION_ENABLED;
  private bypassUsers: string[] = [];
  private configLoaded = false;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL + '/api',
      timeout: 60000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    
    // Proactively fetch config on load (runs asynchronously)
    this.loadEncryptionConfig().catch(() => {});
  }

  /**
   * Fetch the encryption config (bypassed users list and global flag) from the backend.
   */
  private async loadEncryptionConfig() {
    if (this.configLoaded) return;
    try {
      // Use raw axios to avoid triggering our apiClient interceptors recursively
      const response = await axios.get(`${API_BASE_URL}/api/auth/encryption-config`);
      if (response.data) {
        if (typeof response.data.enabled === 'boolean') {
          this.encryptionEnabled = response.data.enabled;
        }
        if (response.data.bypassUsers) {
          this.bypassUsers = response.data.bypassUsers.map((u: string) => u.toLowerCase());
          localStorage.setItem('bypassUsers', JSON.stringify(this.bypassUsers));
        }
        localStorage.setItem('encryptionEnabled', JSON.stringify(this.encryptionEnabled));
        this.configLoaded = true;
      }
    } catch (e) {
      console.error('Failed to load encryption config:', e);
      // Fallback to local storage cache if available
      try {
        const cachedEnabled = localStorage.getItem('encryptionEnabled');
        if (cachedEnabled !== null) {
          this.encryptionEnabled = JSON.parse(cachedEnabled);
        }
        const cached = localStorage.getItem('bypassUsers');
        if (cached) {
          this.bypassUsers = JSON.parse(cached);
        }
        this.configLoaded = true;
      } catch (_) {}
    }
  }

  private setupInterceptors() {
    // Request interceptor to add tenant context, authorization, and payload encryption
    this.instance.interceptors.request.use(
      async (config) => {
        // Ensure encryption configurations are loaded
        if (!this.configLoaded) {
          await this.loadEncryptionConfig();
        }

        // 1. Tenant context for multi-tenancy
        try {
          const tenantId = localStorage.getItem('tenantId');
          const tenantCode = localStorage.getItem('tenantCode');
          if (tenantId) {
            config.headers['X-Tenant-Id'] = tenantId;
          } else if (tenantCode) {
            config.headers['X-Tenant-Code'] = tenantCode;
          } else if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1') {
              const parts = hostname.split('.');
              const subdomain = parts.length >= 2 && parts[0] !== 'www' ? parts[0] : null;
              if (subdomain) config.headers['X-Tenant-Code'] = subdomain;
            }
          }

          // Restore Authorization header fallback
          const token = localStorage.getItem('accessToken');
          if (token && token !== 'null' && token !== 'undefined') {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (_) { }

        // 2. Super Admin Tenant filter check
        try {
          const user = localStorage.getItem('user');
          if (user) {
            const parsedUser = JSON.parse(user);
            const isSuperAdmin = parsedUser.roles?.some((role: string) =>
              role.toUpperCase().includes('SUPER_ADMIN') || role.toUpperCase().includes('SUPERADMIN')
            );

            if (isSuperAdmin) {
              const savedFilters = localStorage.getItem('superAdminFilters');
              if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.selectedTenantId && filters.selectedTenantId !== 'all') {
                  config.params = {
                    ...config.params,
                    tenant_id: filters.selectedTenantId
                  };
                }
              }
            }
          }
        } catch (e) {
          console.error('Error processing Super Admin tenant filter:', e);
        }

        // 3. Payload Encryption
        try {
          const path = config.url || '';
          
          // Determine if this is a skipped route
          const isSkipRoute = [
            '/auth/encryption-config',
            '/auth/refresh-token',
            '/health',
            '/cron',
            '/public/careers'
          ].some(prefix => path.startsWith(prefix));

          const isWriteMethod = ['post', 'put', 'patch'].includes((config.method || '').toLowerCase());
          const hasBody = config.data !== undefined && config.data !== null;
          const contentType = config.headers?.['Content-Type'];
          const isMultipart = (typeof contentType === 'string' && contentType.startsWith('multipart/form-data')) || config.data instanceof FormData;

          let shouldEncrypt = this.encryptionEnabled && !isSkipRoute && isWriteMethod && hasBody && !isMultipart;

          if (shouldEncrypt) {
            let currentUsername = '';
            
            // Check username from body if logging in, otherwise check logged-in user session
            if (path.startsWith('/auth/login') && config.data) {
              currentUsername = config.data.username || config.data.user_name || '';
            } else {
              try {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                  const userObj = JSON.parse(userStr);
                  currentUsername = userObj.username || userObj.user_name || '';
                }
              } catch (_) {}
            }

            const isBypassed = currentUsername && this.bypassUsers.includes(currentUsername.toLowerCase());
            if (isBypassed) {
              shouldEncrypt = false;
            }
          }

          if (shouldEncrypt) {
            const plaintext = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
            const encryptedData = await encryptPayload(plaintext);
            const signature = await generateSignature(encryptedData, HASH_SALT);

            config.data = { encryptedData };
            config.headers['X-Payload-Encrypted'] = 'true';
            config.headers['X-Payload-Signature'] = signature;
          } else {
            config.headers['X-Payload-Encrypted'] = 'false';
          }
        } catch (err) {
          console.error('Request payload encryption failed:', err);
          return Promise.reject(err);
        }

        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle payload decryption & token refresh
    this.instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        const isEncrypted = response.headers['x-payload-encrypted'] === 'true' || response.headers['X-Payload-Encrypted'] === 'true';
        if (isEncrypted && response.data?.encryptedData) {
          try {
            const signature = response.headers['x-payload-signature'] || response.headers['X-Payload-Signature'];
            const expectedSignature = await generateSignature(response.data.encryptedData, HASH_SALT);
            
            if (signature && signature !== expectedSignature) {
              throw new Error('Response payload signature verification failed');
            }

            const decryptedText = await decryptPayload(response.data.encryptedData);
            response.data = JSON.parse(decryptedText);
          } catch (err) {
            console.error('Response decryption failed:', err);
            return Promise.reject(err);
          }
        }
        return response;
      },
      async (error) => {
        console.log('Response interceptor - Error status:', error.response?.status);
        console.log('Response interceptor - Error config:', error.config?.url);

        const originalRequest = error.config;
        
        // 1. Decrypt error response if it is encrypted
        const response = error.response;
        if (response) {
          const isEncrypted = response.headers['x-payload-encrypted'] === 'true' || response.headers['X-Payload-Encrypted'] === 'true';
          if (isEncrypted && response.data?.encryptedData) {
            try {
              const decryptedText = await decryptPayload(response.data.encryptedData);
              response.data = JSON.parse(decryptedText);
            } catch (err) {
              console.error('Error response decryption failed:', err);
            }
          }
        }

        // 2. Token refresh handler
        const isTokenError =
          error.response?.status === 401 ||
          (error.response?.status === 403 &&
            /token|expired|invalid|unauthorized/i.test(error.response?.data?.message || ''));

        if (isTokenError && !originalRequest._retry) {
          console.log(
            error.response?.status === 401 ? '401 error detected' : '403 token-related error detected',
            ', attempting token refresh...'
          );

          if (this.isRefreshing) {
            console.log('Token refresh already in progress, queuing request...');
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              if (token && typeof token === 'string') {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return this.instance(originalRequest);
            }).catch((err) => {
              return Promise.reject(err);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            console.log('Calling refresh token endpoint...');
            const response = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`, {}, {
              withCredentials: true
            });

            console.log('Refresh token response:', response.data);

            if (response.data && response.data.success) {
              const { accessToken } = response.data;

              if (accessToken) {
                localStorage.setItem('accessToken', accessToken);
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              }

              this.isRefreshing = false;
              this.processQueue(null, accessToken);

              return this.instance(originalRequest);
            } else {
              throw new Error('Token refresh failed');
            }
          } catch (refreshError) {
            this.isRefreshing = false;
            this.processQueue(refreshError, null);
            this.handleAuthError();
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: unknown, success: boolean | string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(success);
      }
    });

    this.failedQueue = [];
  }

  private handleAuthError() {
    console.log('Handling auth error - token expired or invalid');
    tokenExpirationHandler.handleTokenExpiration();
  }

  // HTTP methods
  async get(url: string, config?: AxiosRequestConfig) {
    return this.instance.get(url, config);
  }

  async post(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.post(url, data, config);
  }

  async put(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.put(url, data, config);
  }

  async delete(url: string, config?: AxiosRequestConfig) {
    return this.instance.delete(url, config);
  }

  async patch(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.patch(url, data, config);
  }

  async downloadFile(path: string) {
    let cleanPath = path;
    if (cleanPath.startsWith('/api/')) cleanPath = cleanPath.substring(5);
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    
    return this.instance.get(`/${cleanPath}`, { responseType: 'blob' });
  }
}

export default new ApiClient();